import { useTranslation } from 'react-i18next';

import { Skeleton } from '../../components/ui';
import {
  useBotToggle,
  useHlBalance,
  useMe,
  useMyBalances,
  useMyPmMarkets,
  usePositions,
} from '../../hooks/useApiQuery';
import { useInfiniteActivity } from '../../hooks/useInfiniteActivity';
import type { UserProfile } from '../../lib/types';
import {
  AllocationCard,
  BotPulse,
  BotToggle,
  EquityHero,
  HlSummary,
  KpiStrip,
  PmMarketsTabbed,
} from '../dashboard/panels';
import { HlPositions, PmPositions, RecentActivity } from '../dashboard/tables';
import { Panel } from '../parts';

function botFlags(me: UserProfile | undefined, bot: 'pm' | 'hl') {
  const ks = me?.kill_switches;
  const halted = !!(ks?.trading_paused || ks?.is_banned);
  const state = me?.bots[bot];
  const botPaused = bot === 'pm' ? ks?.pm_trading_paused : ks?.hl_trading_paused;
  const paused = halted || !!state?.admin_paused || !!botPaused;
  const enabled = !!state?.enabled;
  return { enabled, available: !!state?.available, paused, active: enabled && !paused };
}

/**
 * Web cabinet dashboard. Portfolio summary up top (live equity curve,
 * allocation, KPIs), then PM and HL as their own self-contained blocks —
 * each with an enable/disable toggle, its own live data (PM markets with
 * PTB/move/token-prices + positions; HL account + live mark/unrealized
 * positions) — and a recent-activity stream that links into Activity.
 */
export function CabDashboard() {
  const { t, i18n } = useTranslation();
  const balances = useMyBalances();
  const open = usePositions({ status: 'open' });
  const closed = usePositions({ status: 'closed' });
  const hl = useHlBalance();
  const me = useMe();
  const markets = useMyPmMarkets();
  const activity = useInfiniteActivity({ type: 'all' });
  const toggle = useBotToggle();

  const items = activity.data?.pages.flatMap((p) => p.items) ?? [];
  const counts = activity.data?.pages[0]?.counts;
  const mkts = markets.data?.items;
  const positions = open.data?.items ?? [];
  const pm = botFlags(me.data, 'pm');
  const hlf = botFlags(me.data, 'hl');

  return (
    <div className="space-y-5">
      {/* Portfolio summary */}
      <div className="grid gap-5 lg:grid-cols-[1.7fr_1fr]">
        {balances.isLoading ? (
          <Skeleton className="h-[260px] rounded-pb-lg" />
        ) : (
          <EquityHero balances={balances.data} closed={closed.data?.items} />
        )}
        <AllocationCard balances={balances.data} />
      </div>

      <KpiStrip closed={closed.data} counts={counts} />

      {/* Polymarket */}
      <Panel
        title={
          <span className="flex items-center gap-2">
            <BotPulse active={pm.active} /> Polymarket
          </span>
        }
        action={
          <BotToggle
            enabled={pm.enabled}
            available={pm.available}
            paused={pm.paused}
            busy={toggle.isPending}
            onToggle={(next) => toggle.mutate({ pm: next })}
          />
        }
      >
        <PmMarketsTabbed markets={mkts} />
        <div className="mt-5">
          <div className="pb-eyebrow mb-2">{t('cabinet.open_positions')}</div>
          <PmPositions positions={positions} markets={mkts} />
        </div>
      </Panel>

      {/* Hyperliquid */}
      <Panel
        title={
          <span className="flex items-center gap-2">
            <BotPulse active={hlf.active} /> Hyperliquid
          </span>
        }
        action={
          <BotToggle
            enabled={hlf.enabled}
            available={hlf.available}
            paused={hlf.paused}
            busy={toggle.isPending}
            onToggle={(next) => toggle.mutate({ hl: next })}
          />
        }
      >
        <HlSummary hl={hl.data} />
        <div className="mt-5">
          <div className="pb-eyebrow mb-2">{t('cabinet.open_positions')}</div>
          <HlPositions positions={positions} hl={hl.data} />
        </div>
      </Panel>

      <RecentActivity items={items} lang={i18n.language} />
    </div>
  );
}
